import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Kitchen3dController as TdController } from '@rta/rta-blueprint-3d-component';

import { Actions } from './actions';
import { ActionTypes } from './action-types';
import { State } from './state.type';

export const reducer = (state: State, action: Actions): State => {
	let newState: State = {
		email: state.email,
		isLoading: state.isLoading,
		isLoadingMessage: state.isLoadingMessage,
		hasLoaded: state.hasLoaded,
		designs: [...state.designs],
		page: state.page,
		pageCount: state.pageCount,
		totalCount: state.totalCount,
		isLoadingMore: state.isLoadingMore,
		index: state.index
	};
	// let bpState: DesignState | null = null;
	// let skipped = false;
	// console.log(`reducer:debug -> ${action?.type}`, { state, action });

	switch (action?.type) {
		case ActionTypes.addBlueprint:
			(() => {
				// a design pulled in twice (deep link, then the page it sits on) must not become two tabs
				if (newState.designs.some((d) => d.blueprint.id === action.payload.blueprint.id)) return;

				const bpController = new BpController(action.payload.blueprint, { eventGroup: `bp-${action.payload.blueprint.id}` });
				if (bpController.blueprint) {
					const latestVersion = bpController.blueprint.kitchens.getLatestVersion();

					// console.log('reducer:delete', { delete: bpController.blueprint.delete });

					// create the edit version if version is latest
					let active = null;
					if (action.payload.version === null) {
						active = bpController.blueprint.kitchens.getActiveVersion();
					} else if (action.payload.useDraft) {
						active = bpController.blueprint.kitchens.findVersion(null);
					} else {
						active = bpController.blueprint.kitchens.findVersion(action.payload.version);
					}

					// if requested version is not found, load lastest version
					if (!active) active = latestVersion;

					bpController.kitchenVersion = active.version;
					// Force draft in editor: if a concrete version is active, switch to draft
					if (bpController.kitchenVersion !== null) bpController.kitchenVersion = null;
					// console.log('appNav:addBlueprint', {
					// useDraft: action.payload.useDraft,
					// kitchenVersion: bpController.kitchenVersion, active, version: action.payload.version, kitchens: bpController.blueprint.kitchens
					// });

					const tdController = new TdController(action.payload.blueprint, { eventGroup: `td-${action.payload.blueprint.id}` });

					// kitchen.isLocal is the canonical unsaved-changes flag; isDirty only ever mirrors it
					const isDirty = action.payload.isDirty || bpController.kitchen.isLocal;
					if (isDirty) bpController.kitchen.isLocal = true;

					newState.designs.push({
						blueprint: action.payload.blueprint,
						isDirty,
						bpController,
						tdController
					});

					// Do not write readonly here; controller.readonly should be the source of truth.
					bpController.options.contextMenu = true;
					bpController.options.assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL;
					tdController.options.assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL;
					bpController.viewport.autoZoom = null;
				}
			})();

			break;
		case ActionTypes.clearCustomerDesigns:
			newState.email = null;
			newState.isLoading = false;
			newState.isLoadingMessage = '';
			newState.hasLoaded = false;
			newState.designs = [];
			newState.page = 0;
			newState.pageCount = 0;
			newState.totalCount = 0;
			newState.isLoadingMore = false;
			newState.index = [];
			break;
		case ActionTypes.deleteBlueprint:
			newState.designs = newState.designs.filter((bp) => bp.blueprint.id !== action.payload);
			newState.index = newState.index.filter((entry) => entry.id !== action.payload);
			newState.totalCount = Math.max(newState.index.length, newState.designs.length);
			break;
		case ActionTypes.loadBlueprintsStart:
			newState.designs = [];
			newState.page = 0;
			newState.pageCount = 0;
			newState.totalCount = 0;
			newState.isLoadingMore = false;
			newState.index = [];
			newState.isLoading = true;
			newState.isLoadingMessage = action.payload.message;
			newState.hasLoaded = false;
			newState.email = action.payload.email;
			break;
		case ActionTypes.loadBlueprintsSuccess:
			newState.isLoading = false;
			newState.isLoadingMessage = '';
			newState.hasLoaded = true;
			break;
		case ActionTypes.loadBlueprintsFailure:
			newState.isLoading = false;
			newState.isLoadingMessage = '';
			newState.hasLoaded = false;
			break;
		case ActionTypes.loadMoreStart:
			newState.isLoadingMore = true;
			break;
		case ActionTypes.loadMoreEnd:
			newState.isLoadingMore = false;
			break;
		case ActionTypes.setDesignPage:
			(() => {
				const { page, pageCount, totalCount, index } = action.payload;
				// a late page-1 response must never walk the page counter backwards past a page the user already pulled in
				newState.page = Math.max(newState.page, page);
				newState.pageCount = pageCount;
				newState.totalCount = totalCount;
				newState.index = index;
			})();
			break;
		case ActionTypes.setDesignPriorities:
			(() => {
				const priorities = action.payload;
				newState.designs.forEach((design) => {
					const priority = priorities[design.blueprint.id];
					if (priority !== undefined) design.blueprint.metadata.priority = priority;
				});
				newState.index = newState.index
					.map((entry) => (priorities[entry.id] === undefined ? entry : { ...entry, priority: priorities[entry.id] }))
					.sort((a, b) => (a.priority || Infinity) - (b.priority || Infinity) || Number(b.submissionDate) - Number(a.submissionDate));
			})();
			break;
		case ActionTypes.renderingUpdated:
			// blueprint.thumbnail is a plain unpublished property, so a capture is invisible to
			// consumers memoised on the designs array. This rebuilds that array's identity.
			break;
		case ActionTypes.setIsDirty:
			(() => {
				const design = newState.designs.find((d) => d.blueprint.id === action.payload.id);
				if (design) {
					const { isDirty } = action.payload;
					design.isDirty = isDirty;
					// single funnel for every dirty transition, so no call site can leave the two in disagreement
					if (design.bpController) design.bpController.kitchen.isLocal = isDirty;
					// a dirty transition IS the mutation signal — stamping it here is what makes an already-captured rendering go stale
					if (isDirty && design.bpController) design.bpController.kitchen.lastMutatedAt = Date.now();
				}
			})();
			break;
		default:
			// skipped = true;
			newState = state;
	}

	// console.log(`reducer:debug -> ${action?.type}`, { oldState: state, newState, action });
	// if (!skipped) console.log(`reducer:debug -> ${action.type}`, { state, newState });
	return newState;
};
